"use client";

import { memo, useEffect, useRef, useState } from "react";
import Link from "next/link";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import DeployButton from "@/components/ui/DeployButton";

interface ChainStat { name: string; block: number; latency: number }

// Isolated + memoized so a reqRate/label tick (every 1.5s) doesn't re-render
// all 30 bars — only a sparkline update (also every 1.5s, but a distinct
// array reference) does.
const SparklineBars = memo(function SparklineBars({ sparkline }: { sparkline: number[] }) {
  return (
    <div className="flex h-10 items-end gap-[2px]">
      {sparkline.map((l, i) => {
        const pct = Math.min(1, l / 110);
        const progress = i / (sparkline.length - 1);
        return (
          <div
            key={i}
            className="flex-1 rounded-t-[2px] bg-accent"
            style={{
              height: `${pct * 100}%`,
              opacity: 0.35 + progress * 0.55,
            }}
          />
        );
      })}
    </div>
  );
});
// Deterministic seed for the chain table so the dashboard renders its full
// content in the server HTML. Without this the table was empty until the client
// `/api/chain-stats` fetch resolved — that late paint became the LCP element
// under Lighthouse's throttled simulation (LCP ~6.5s). Rendering rows on the
// server makes the largest paint happen immediately; live data replaces these
// on mount. Values mirror the route's seed (data/route stay loosely in sync).
const CHAIN_SEED: ChainStat[] = [
  { name: "Ethereum", block: 25_093_574,  latency: 42 },
  { name: "Arbitrum", block: 462_748_892, latency: 38 },
  { name: "Polygon",  block: 86_871_078,  latency: 51 },
  { name: "Optimism", block: 151_582_659, latency: 45 },
  { name: "Base",     block: 45_987_374,  latency: 40 },
];

/* ── Live, ticking dashboard ─────────────────────────────────────────────── */
function MiniDashboard() {
  // Deterministic SSR seed — replaced with randomized data after mount to avoid
  // hydration mismatch from Math.random() executing on server and client.
  const [sparkline, setSparkline] = useState<number[]>(() =>
    Array.from({ length: 30 }, (_, i) => 80 + ((i * 7) % 18)),
  );
  const [reqRate, setReqRate] = useState(3_842);
  const [labelIdx, setLabelIdx] = useState(0);
  const [chainStats, setChainStats] = useState<ChainStat[]>(CHAIN_SEED);
  const reduceMotion = useRef(false);

  // Fetch chain stats (live or estimated) and refresh every 12s.
  // The server always returns data — either live from RPC or +1 from the last
  // known block — so the client just applies whatever it receives.
  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await fetch("/api/chain-stats");
        if (res.ok) {
          const data: ChainStat[] = await res.json();
          if (Array.isArray(data) && data.length) setChainStats(data);
        }
      } catch {}
    }
    fetchStats();
    const poll = setInterval(fetchStats, 12_000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    const initId = setTimeout(() => setSparkline(Array.from({ length: 30 }, () => 72 + Math.random() * 24)), 0);

    reduceMotion.current = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion.current) return () => clearTimeout(initId);

    let id: ReturnType<typeof setInterval> | undefined;
    let labelTimer: ReturnType<typeof setInterval> | undefined;

    // Don't compete with LCP/hydration on the most performance-sensitive
    // component on the page — start the ticking dashboard once the browser
    // has a spare moment, not the instant the hero mounts.
    const start = () => {
      id = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        setReqRate((v) => {
          // Mean-reverting random walk — drifts back toward 3,842 so it
          // fluctuates naturally without climbing forever
          const BASE = 3_842;
          const pull = (BASE - v) * 0.08;           // gentle pull back to base
          const noise = (Math.random() - 0.5) * 120; // ±60 random swing per tick
          return Math.round(Math.max(2_800, Math.min(5_200, v + pull + noise)));
        });
      }, 1500); // was 800ms — halves the re-render churn during the TBT window

      labelTimer = setInterval(() => setLabelIdx((i) => i + 1), 2600);
    };

    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    const idleHandle = ric ? ric(start, { timeout: 2000 }) : window.setTimeout(start, 1000);

    return () => {
      clearTimeout(initId);
      if (id) clearInterval(id);
      if (labelTimer) clearInterval(labelTimer);
      const cic = (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback;
      if (cic) cic(idleHandle as number);
      else clearTimeout(idleHandle as number);
    };
  }, []);

  const ethBlock = chainStats[0]?.block ?? 0;
  const avgLat = chainStats.length
    ? Math.round(chainStats.reduce((a, c) => a + c.latency, 0) / chainStats.length)
    : 0;

  const labels = [
    `Routing ${reqRate.toLocaleString('en-US')} req/min`,
    `ETH · #${ethBlock.toLocaleString('en-US')}`,
    `p99 ${avgLat}ms · 5 regions`,
    `47 chains synced`,
  ];
  const liveLabel = labels[labelIdx % labels.length];

  const chains = chainStats.map((c) => ({
    name: c.name,
    blocks: c.block,
    latency: c.latency > 0 ? `${c.latency}ms` : "—",
  }));

  return (
    <div
      className="w-full max-w-[520px] overflow-hidden rounded-lg border border-line bg-surface-2"
      style={{ boxShadow: "var(--shadow-md)" }}
    >
      {/* Window bar */}
      <div className="flex items-center justify-between border-b border-line bg-surface px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full bg-good"
            style={{ boxShadow: "0 0 0 3px color-mix(in oklab, var(--good) 18%, transparent)" }}
          />
          <span
            key={labelIdx}
            className="font-mono text-[11px] tracking-[0.06em] text-ink-2 tabular-nums"
            style={{ animation: "wIn 320ms cubic-bezier(0.2,0,0,1) both" }}
          >
            {liveLabel}
          </span>
        </div>
        <span className="font-mono text-[10px] tracking-[0.1em] text-ink-2">LIVE</span>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-3 border-b border-line">
        {[
          { label: "REQ/MIN",     value: reqRate.toLocaleString('en-US') },
          { label: "P99 LATENCY", value: `${avgLat}ms` },
          { label: "UPTIME 90D",  value: "99.95%" },
        ].map((m, i) => (
          <div
            key={m.label}
            className={`px-4 py-3.5 ${i < 2 ? "border-r border-line" : ""}`}
          >
            <div className="font-mono text-[9px] tracking-[0.12em] text-ink-2 mb-1">
              {m.label}
            </div>
            <div className="font-mono text-[17px] font-semibold tracking-tight text-ink tabular-nums">
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* Latency sparkline */}
      <div className="border-b border-line px-4 py-3.5">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[9px] tracking-[0.12em] text-ink-2">
            LATENCY (30s)
          </span>
          <span className="font-mono text-[10px] text-ink-2">{avgLat}ms avg</span>
        </div>
        <SparklineBars sparkline={sparkline} />
      </div>

      {/* Chain status table */}
      <div>
        <div className="flex items-center justify-between px-4 pt-2.5 pb-1.5">
          <span className="font-mono text-[9px] tracking-[0.12em] text-ink-2">CHAIN</span>
          <div className="flex gap-10">
            <span className="w-[70px] text-right font-mono text-[9px] tracking-[0.12em] text-ink-2">
              BLOCK
            </span>
            <span className="w-[45px] text-right font-mono text-[9px] tracking-[0.12em] text-ink-2">
              P99
            </span>
          </div>
        </div>
        {chains.map((c) => (
          <div
            key={c.name}
            className="flex items-center justify-between border-t border-line-soft px-4 py-2"
          >
            <div className="flex items-center gap-2">
              <span className="h-[5px] w-[5px] rounded-full bg-good" />
              <span className="text-[12px] text-ink">{c.name}</span>
            </div>
            <div className="flex items-center gap-10">
              <span className="w-[70px] text-right font-mono text-[11px] text-ink-2 tabular-nums">
                {(c.blocks ?? 0).toLocaleString('en-US')}
              </span>
              <span className="w-[45px] text-right font-mono text-[11px] text-ink-2">
                {c.latency}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Hero ────────────────────────────────────────────────────────────────── */
export default function HeroSection() {
  return (
    <header className="relative overflow-hidden border-b border-line py-[68px] sm:py-[88px]">
      {/* Aurora animated background */}

      {/* Accent radial glow on top of aurora */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 80% 50%, color-mix(in oklab, var(--accent) 6%, transparent) 0%, transparent 60%)",
        }}
      />

      <Container>
        <div className="relative z-10 grid items-start gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:min-h-[480px]">
          {/* Left: copy */}
          <div>
            {/* Status pill */}
            <Link
              href="/node-status"
              className="mb-6 inline-flex items-center gap-2 hero-fade"
              style={{ animation: "wIn 500ms 60ms cubic-bezier(0.2,0,0,1) both" }}
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-good" />
              <span className="font-mono text-[11px] tracking-[0.1em] text-ink-2">
                ALL SYSTEMS OPERATIONAL
              </span>
            </Link>

            {/* Headline — italic Newsreader for emphasis (NOT gradient text).
                No entrance animation: headline is the LCP element, so an opacity:0
                start state delays the paint Lighthouse measures. Other hero bits
                still fade in. */}
            <h1
              className="m-0 font-accent font-normal tracking-[-0.03em] leading-[0.97] text-ink"
              style={{
                fontSize: "clamp(44px, 5.5vw, 72px)",
              }}
            >
              Infrastructure that{" "}
              <em className="not-italic font-accent italic text-accent">
                never sleeps,
              </em>{" "}
              so your team can.
            </h1>

            {/* Tweak A: CTAs promoted directly under the headline for upper-third
                visibility. Supporting copy follows as reinforcement. Entrance
                stagger reordered to match the new visual order. */}
            <div
              className="mt-8 flex flex-wrap items-center gap-2.5"
              style={{ animation: "wIn 500ms 150ms cubic-bezier(0.2,0,0,1) both" }}
            >
              <DeployButton
                size="lg"
                location="hero"
                label="Deploy Your Node in Under 5 Minutes"
              />
              <Button
                href="/contact-us"
                variant="soft"
                size="lg"
                data-cta="talk-to-engineer"
                data-cta-location="hero"
              >
                Talk to An Expert
              </Button>
            </div>

            <p
              className="mt-7 max-w-[500px] text-[18px] leading-[1.52] text-ink-2"
              style={{ animation: "wIn 500ms 200ms cubic-bezier(0.2,0,0,1) both" }}
            >
              Managed RPC endpoints, validator nodes, and dedicated infrastructure across 50+ blockchains — with multi-region failover and SOC 2 Type II compliance. No servers to operate.
            </p>

            <p
              className="mt-3 max-w-[500px] font-mono text-[12px] tracking-[0.12em] uppercase text-ink-3"
              style={{ animation: "wIn 500ms 250ms cubic-bezier(0.2,0,0,1) both" }}
            >
              Node as a Service · Validator Nodes · RPC Endpoints
            </p>
          </div>

          {/* Right: live dashboard */}
          <div
            className="flex justify-center lg:justify-end"
            style={{ animation: "wIn 600ms 200ms cubic-bezier(0.2,0,0,1) both" }}
          >
            <MiniDashboard />
          </div>
        </div>

        {/* Metric strip — full-width centered row below the hero grid.
            2×2 on mobile, single centered row on sm+. Larger values for prominence. */}
        <div
          className="relative z-10 mt-14 grid grid-cols-2 gap-y-8 border-t border-line pt-10 text-center sm:mt-16 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-y-0"
          style={{ animation: "wIn 500ms 360ms cubic-bezier(0.2,0,0,1) both" }}
        >
          {[
            { v: "99.95", s: "%",  l: "Uptime SLA" },
            { v: "87",    s: "ms", l: "P99 · 24h" },
            { v: "50",    s: "+",  l: "Networks" },
            { v: "24",    s: "",   l: "Regions" },
          ].map((m, i, arr) => (
            <div
              key={m.l}
              className={`flex flex-col items-center gap-1.5 ${
                i < arr.length - 1 ? "sm:mr-12 sm:border-r sm:border-line sm:pr-12" : ""
              }`}
            >
              <span className="font-mono text-[30px] font-medium tracking-[-0.01em] leading-none text-ink sm:text-[36px]">
                {m.v}
                <small className="ml-[2px] text-[15px] font-normal text-ink-3 sm:text-[17px]">
                  {m.s}
                </small>
              </span>
              <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-3">
                {m.l}
              </span>
            </div>
          ))}
        </div>
      </Container>

      {/* Hero-scoped keyframes — match the v2 design's "wIn" reveal */}
      <style jsx>{`
        @keyframes wIn {
          from { opacity: 0; transform: translateY(0.3em); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; }
        }
      `}</style>
    </header>
  );
}
